// FileScopeModal.tsx — the V6 "Assign" modal: which products use this file?
// Design: docs/design/design_handoff_files_v6 (README §2, files-v2-v6.jsx).
// Files scope to whole products only; saving clears any legacy feature scopes.
import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Dialog, Typography } from "@mui/material";
import { Check, Globe, Pin, X } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { useAppSelector } from "@/state/hooks";
import { RootState } from "@/state/store";
import { VectorFile } from "@/api/models/vector_file";
import fetchWithAuth from "@/api/secureFetch";
import { FileTypeTile, ProductGlyph, productColor, T } from "./filesV6";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

export interface ScopeChanges {
    added_project_ids: number[];
    removed_project_ids: number[];
    added_feature_ids: number[];
    removed_feature_ids: number[];
}

export interface SavedScopeData {
    fileid: number;
    is_global: boolean;
    company_id: number | null;
    project_ids: number[];
    feature_ids: number[];
    changes: ScopeChanges;
}

interface FileScopeModalProps {
    open: boolean;
    file: VectorFile | null;
    currentScope: {
        global: { company_id: number; company_name: string } | null;
        projects: Array<{ project_id: number; project_name: string }>;
        features: Array<{ feature_id: number; feature_name: string }>;
    } | null;
    onClose: () => void;
    onSave: (savedScope: SavedScopeData) => void;
}

interface ProductListItem {
    project_id: number;
    project_name: string;
    features: number;
}

const FileScopeModal: React.FC<FileScopeModalProps> = ({ open, file, currentScope, onClose, onSave }) => {
    const { getToken } = useAuth();
    const userProfile = useAppSelector((state: RootState) => state.userProfiles?.userProfile);

    const [products, setProducts] = useState<ProductListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isGlobal, setIsGlobal] = useState(false);
    // picked survives the All-products switch being toggled on and off, so the
    // previous selection restores when the switch goes back off (spec §4).
    const [picked, setPicked] = useState<Set<number>>(new Set());
    const [hasFetchedProducts, setHasFetchedProducts] = useState(false);

    // Hydrate from the file's current scope each time the modal opens.
    useEffect(() => {
        if (!open) return;
        setIsGlobal(!!currentScope?.global);
        setPicked(new Set(currentScope?.projects.map(p => p.project_id) ?? []));
    }, [open, currentScope]);

    // Product list (with feature counts) from the enriched dashboard endpoint.
    // Guarded by hasFetchedProducts: getToken gets a fresh identity every render
    // (Clerk), so depending on it without a guard would refetch after each render.
    useEffect(() => {
        if (!open) {
            setHasFetchedProducts(false);
            return;
        }
        if (hasFetchedProducts) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const token = await getToken();
                if (!token) {
                    console.error("FileScopeModal: no auth token available");
                    return;
                }
                const response = await fetchWithAuth(`${BACKEND_URI}/api/user/projects/list`, token);
                if (!response.ok) throw new Error(`Failed to fetch products: ${response.statusText}`);
                const data = await response.json();
                if (!cancelled) {
                    setProducts(
                        (data.projects || []).map((p: Record<string, unknown>) => ({
                            project_id: p.project_id as number,
                            project_name: p.project_name as string,
                            features: (p.features as number) ?? 0
                        }))
                    );
                }
            } catch (err) {
                console.error("FileScopeModal: error fetching products:", err);
            } finally {
                if (!cancelled) {
                    setHasFetchedProducts(true);
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, hasFetchedProducts, getToken]);

    const valid = isGlobal || picked.size > 0;

    const togglePick = (id: number) =>
        setPicked(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const handleSave = async () => {
        if (!file || !valid || saving) return;
        setSaving(true);
        try {
            const token = await getToken();
            if (!token) throw new Error("No auth token available");

            let companyId: number | null = null;
            if (isGlobal) {
                companyId = userProfile?.companyid ?? null;
                if (!companyId) throw new Error("Company ID not available in user profile. Cannot make file available to all products.");
            }

            const originalProjectIds = new Set(currentScope?.projects.map(p => p.project_id) ?? []);
            const projectIds = isGlobal ? [] : Array.from(picked);

            const payload: SavedScopeData = {
                fileid: file.fileid,
                is_global: isGlobal,
                company_id: companyId,
                project_ids: projectIds,
                // Product level is the floor and ceiling — saving clears any
                // legacy feature-level scopes (spec "Decisions" §3).
                feature_ids: [],
                changes: {
                    added_project_ids: projectIds.filter(id => !originalProjectIds.has(id)),
                    removed_project_ids: Array.from(originalProjectIds).filter(id => !projectIds.includes(id)),
                    added_feature_ids: [],
                    removed_feature_ids: (currentScope?.features ?? []).map(f => f.feature_id)
                }
            };

            const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/update_file_scopes`, token, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const responseBody = await response.json().catch(() => null);
            if (!response.ok) {
                const message =
                    responseBody && typeof responseBody.error === "string"
                        ? responseBody.error
                        : `Request failed with status ${response.status}`;
                throw new Error(message);
            }
            if (responseBody?.context_mesh_refresh?.scheduled === false) {
                console.warn("File scopes saved, but Context Mesh refresh was not scheduled.", {
                    requestId: responseBody.context_mesh_refresh.request_id
                });
            }

            onSave(payload);
            onClose();
        } catch (err) {
            console.error("Error saving file scopes:", err);
            alert(`Failed to save file scopes: ${err}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="file-scope-title"
            slotProps={{ backdrop: { sx: { backgroundColor: "rgba(15,18,32,0.42)", backdropFilter: "blur(2px)" } } }}
            PaperProps={{
                sx: {
                    width: 560,
                    maxWidth: "92vw",
                    maxHeight: 700,
                    borderRadius: "18px",
                    boxShadow: "0 30px 80px rgba(15,18,32,0.4)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    backgroundColor: T.bgPaper
                }
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "18px 22px", borderBottom: `1px solid ${T.divider}`, flex: "0 0 auto" }}>
                <FileTypeTile filename={file?.filename} size={36} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography id="file-scope-title" sx={{ fontSize: 16, fontWeight: 600, color: T.fg1 }}>
                        Which products use this file?
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: T.fg4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file?.filename}
                    </Typography>
                </Box>
                <Box
                    component="button"
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        width: 30, height: 30, borderRadius: "8px", border: `1px solid ${T.divider}`,
                        backgroundColor: T.bgPaper, color: T.fg3, cursor: "pointer", p: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto",
                        "&:hover": { backgroundColor: T.bgPanel }
                    }}
                >
                    <X size={15} />
                </Box>
            </Box>

            {/* All products switch row */}
            <Box
                role="switch"
                aria-checked={isGlobal}
                aria-label="All products"
                tabIndex={0}
                onClick={() => setIsGlobal(g => !g)}
                onKeyDown={e => {
                    if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setIsGlobal(g => !g);
                    }
                }}
                sx={{
                    display: "flex", alignItems: "center", gap: "12px", p: "14px 22px", cursor: "pointer",
                    backgroundColor: isGlobal ? T.spineSoft : "transparent",
                    borderBottom: `1px solid ${T.divider}`, flex: "0 0 auto",
                    "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: "-2px" }
                }}
            >
                <Globe size={16} color={isGlobal ? T.spineDeep : T.fg3} style={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: isGlobal ? T.spineDeep : T.fg1 }}>All products</Typography>
                    <Typography sx={{ fontSize: 11.5, color: T.fg4 }}>
                        Every product — current and future — can read it. Best for company-wide context.
                    </Typography>
                </Box>
                <Box component="span" sx={{ width: 38, height: 22, borderRadius: 999, flex: "0 0 auto", backgroundColor: isGlobal ? T.spineDeep : T.divider, position: "relative", transition: "background .15s" }}>
                    <Box
                        component="span"
                        sx={{
                            position: "absolute", top: "2px", left: isGlobal ? "18px" : "2px",
                            width: 18, height: 18, borderRadius: 999, backgroundColor: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.25)", transition: "left .15s"
                        }}
                    />
                </Box>
            </Box>

            {/* Product multi-select */}
            <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", p: "12px 16px", opacity: isGlobal ? 0.4 : 1, pointerEvents: isGlobal ? "none" : "auto" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.fg4, p: "2px 8px 8px" }}>
                    Or pick one or more products
                </Typography>
                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "10px 12px" }}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontSize: 12.5, color: T.fg4 }}>Loading products…</Typography>
                    </Box>
                ) : products.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: T.fg4, p: "10px 12px" }}>No products available</Typography>
                ) : (
                    products.map(p => {
                        const on = picked.has(p.project_id);
                        return (
                            <Box
                                key={p.project_id}
                                role="checkbox"
                                aria-checked={on}
                                aria-label={p.project_name}
                                tabIndex={isGlobal ? -1 : 0}
                                onClick={() => togglePick(p.project_id)}
                                onKeyDown={e => {
                                    if (e.key === " " || e.key === "Enter") {
                                        e.preventDefault();
                                        togglePick(p.project_id);
                                    }
                                }}
                                sx={{
                                    display: "flex", alignItems: "center", gap: "11px", cursor: "pointer",
                                    p: "10px 12px", borderRadius: "10px",
                                    backgroundColor: on ? T.spineSoft : "transparent",
                                    border: `1px solid ${on ? T.spineBorder : "transparent"}`,
                                    "&:hover": { backgroundColor: on ? T.spineSoft : T.bgPanel },
                                    "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: "-2px" }
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: 17, height: 17, borderRadius: "5px", flex: "0 0 auto",
                                        border: `2px solid ${on ? T.spineDeep : T.divider}`,
                                        backgroundColor: on ? T.spineDeep : "transparent",
                                        display: "inline-flex", alignItems: "center", justifyContent: "center"
                                    }}
                                >
                                    {on && <Check size={11} color="#fff" strokeWidth={3} />}
                                </Box>
                                <ProductGlyph color={productColor(p.project_id)} size={15} />
                                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: on ? 600 : 500, color: on ? T.fg1 : T.fg2 }}>
                                    {p.project_name}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: T.fg4 }}>{p.features} features</Typography>
                            </Box>
                        );
                    })
                )}
            </Box>

            {/* Reach line */}
            <Box sx={{ display: "flex", gap: "9px", p: "12px 22px", borderTop: `1px solid ${T.divider}`, backgroundColor: T.bgPanel, flex: "0 0 auto" }}>
                <Pin size={14} color={T.fg4} style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography aria-live="polite" sx={{ fontSize: 11.5, color: T.fg4, lineHeight: 1.5 }}>
                    {valid
                        ? "The AI will read this file making it part of your context mesh."
                        : "Pick at least one product, or switch on All products."}
                </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px", p: "14px 22px", borderTop: `1px solid ${T.divider}`, flex: "0 0 auto" }}>
                <Box
                    component="button"
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        height: 38, px: "16px", borderRadius: "9px", border: `1px solid ${T.divider}`,
                        backgroundColor: T.bgPaper, color: T.fg2, fontSize: 13, fontWeight: 600,
                        cursor: "pointer", fontFamily: "inherit",
                        "&:hover": { backgroundColor: T.bgPanel }
                    }}
                >
                    Cancel
                </Box>
                <Box
                    component="button"
                    onClick={handleSave}
                    disabled={!valid || saving}
                    sx={{
                        height: 38, px: "18px", borderRadius: "9px", border: "none",
                        backgroundColor: valid && !saving ? T.primary : T.divider,
                        color: "#fff", fontSize: 13, fontWeight: 600,
                        cursor: valid && !saving ? "pointer" : "default", fontFamily: "inherit",
                        boxShadow: valid && !saving ? "0 2px 8px rgba(99,102,241,0.3)" : "none",
                        "&:hover": { backgroundColor: valid && !saving ? T.primaryHover : T.divider }
                    }}
                >
                    {saving ? "Saving…" : "Save"}
                </Box>
            </Box>
        </Dialog>
    );
};

export default FileScopeModal;
